import { UseFormReturn } from 'react-hook-form';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { Textarea } from '@/components/ui-kit/textarea';
import { Button } from '@/components/ui-kit/button';
import { Brain, FileJson, FileText, Pencil, Lightbulb } from 'lucide-react';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';

import { useEffect, useRef, useState } from 'react';
import ModelSelection from '@/modules/gpt-chats/components/model-selection-internal/model-selection';
import { ModelParametersModal } from './model-parameters-modal';
import { JsonEditorModal } from './json-editor-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import useIsMobile from '@/hooks/use-is-mobile';
import { useTranslation } from 'react-i18next';

type LlmSectionProps = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  isMobile: boolean;
  onOpenBasePromptAIModal: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onModelSubmit: (values: any) => Promise<void>;
};

export const LlmSection = ({
  form,
  isMobile,
  onOpenBasePromptAIModal,
  onModelSubmit,
}: LlmSectionProps) => {
  const { t } = useTranslation();
  const isXlBreakpoint = useIsMobile();
  const [isModelParametersDropdownOpen, setIsModelParametersDropdownOpen] = useState(false);
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const jsonSchemaRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isResizingJsonSchema, setIsResizingJsonSchema] = useState(false);
  const [tokenCount, setTokenCount] = useState(0);
  const [basePromptValue, setBasePromptValue] = useState('');

  const calculateTokenCount = (text: string) => {
    if (text && text.trim().length > 0) {
      const words = text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      return words.length;
    }
    return 0;
  };

  const watchedBasePrompt = form.watch('llm_config.base_prompt');

  useEffect(() => {
    const prompt = watchedBasePrompt || '';
    setTokenCount(calculateTokenCount(prompt));
    setBasePromptValue(prompt);
  }, [watchedBasePrompt]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !textareaRef.current) return;

      const textarea = textareaRef.current;
      const rect = textarea.getBoundingClientRect();
      const newHeight = e.clientY - rect.top;

      if (newHeight > 60) {
        textarea.style.height = `${newHeight}px`;
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingJsonSchema || !jsonSchemaRef.current) return;

      const schemaDiv = jsonSchemaRef.current;
      const rect = schemaDiv.getBoundingClientRect();
      const newHeight = e.clientY - rect.top;

      if (newHeight > 100) {
        schemaDiv.style.height = `${newHeight}px`;
      }
    };

    const handleMouseUp = () => {
      setIsResizingJsonSchema(false);
    };

    if (isResizingJsonSchema) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingJsonSchema]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleJsonSchemaMouseDown = (e: React.MouseEvent) => {
    setIsResizingJsonSchema(true);
    e.preventDefault();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBasePromptValue(value);
    const newTokenCount = calculateTokenCount(value);
    setTokenCount(newTokenCount);
  };

  const handleTextareaBlur = (field: { onChange: (value: string) => void; onBlur: () => void }) => {
    const currentValue = form.getValues('llm_config.base_prompt');
    if (currentValue !== basePromptValue) {
      field.onChange(basePromptValue);
      onModelSubmit(form.getValues());
    }
  };

  const handleResponseTypeChange = (value: string) => {
    form.setValue('response_type', value);
    if (value === 'json') {
      const dummySchema = JSON.stringify(
        {
          answer: 'string',
          question: 'string',
        },
        null,
        2
      );
      form.setValue('response_format', dummySchema);
    } else {
      form.setValue('response_format', null);
    }
  };

  const handleJsonSchemaSave = async (jsonSchema: string) => {
    form.setValue('response_format', jsonSchema);
    setIsJsonEditorOpen(false);
  };

  const responseType = form.watch('response_type');
  const responseFormat = form.watch('response_format');

  const dummySchema = JSON.stringify(
    {
      answer: 'string',
      question: 'string',
    },
    null,
    2
  );

  const isExampleSchema = responseFormat === dummySchema;

  return (
    <AccordionItem value="model">
      <AccordionTrigger className="px-5 text-lg hover:no-underline">LLM</AccordionTrigger>
      <AccordionContent className="relative px-5">
        <div className="w-full xl:w-1/2">
          <ModelSelection form={form} />
        </div>

        <div className={`grid gap-6 pt-3 ${isXlBreakpoint ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <div className="relative w-full">
            <FormLabel className="mb-3 block text-sm font-medium">{t('MODEL_PARAMETERS')}</FormLabel>
            <div className="flex flex-wrap gap-4" id="model-parameters-buttons">
              <button
                type="button"
                className="cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10"
                onClick={() => setIsModelParametersDropdownOpen(true)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-high-emphasis">{t('TEMPERATURE')}:</span>
                  <span className="text-sm font-semibold text-primary">
                    {Math.min(form.watch('llm_config.temperature') ?? 0.7, 1).toFixed(2)}
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10"
                onClick={() => setIsModelParametersDropdownOpen(true)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-high-emphasis">{t('MAXIMUM_RESPONSE')}:</span>
                  <span className="text-sm font-semibold text-primary">
                    {Math.min(form.watch('llm_config.max_tokens') ?? 10000, 10000)}
                  </span>
                </div>
              </button>
            </div>

            {isModelParametersDropdownOpen && (
              <ModelParametersModal
                form={form}
                onClose={() => setIsModelParametersDropdownOpen(false)}
                isMobile={isMobile}
              />
            )}
          </div>
        </div>

        <div className="mt-4">
          <FormField
            control={form.control}
            name="response_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="mb-3 block text-sm font-medium">{t('RESPONSE_TYPE')}</FormLabel>
                <Select onValueChange={handleResponseTypeChange} value={field.value || 'text'}>
                  <FormControl>
                    <SelectTrigger className="h-9 w-fit min-w-[190px]">
                      <SelectValue placeholder={t('SELECT_RESPONSE_TYPE')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="text">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>{t('TEXT')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="json">
                      <div className="flex items-center gap-2">
                        <FileJson className="h-4 w-4" />
                        <span>{t('JSON')}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        {responseType === 'json' && (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <FormLabel className="flex items-center gap-2 text-sm font-medium">
                {t('RESPONSE_SCHEMA')}
                <InfoTooltip content={t('RESPONSE_SCHEMA_TOOLTIP')} />
              </FormLabel>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsJsonEditorOpen(true)}
                className="flex items-center gap-2 text-sm"
              >
                <Pencil className="h-4 w-4" />
                {t('EDIT_JSON')}
              </Button>
            </div>

            {responseFormat && (
              <div className="space-y-2">
                {isExampleSchema && (
                  <div className="rounded-md border-l-4 border-primary bg-blue-50/95 px-3 py-1.5 dark:bg-blue-950/95">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary dark:text-blue-400" />
                      <p className="text-xs leading-relaxed text-primary dark:text-blue-300">
                        {t('EXAMPLE_SCHEMA_HINT')}
                      </p>
                    </div>
                  </div>
                )}

                <div className="relative">
                  <div
                    ref={jsonSchemaRef}
                    className="overflow-y-auto rounded-lg border border-border bg-muted/30 p-3 pb-8 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
                    style={{
                      height: '150px',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgb(156 163 175) transparent',
                    }}
                  >
                    <pre className="whitespace-pre-wrap break-words text-sm text-medium-emphasis">
                      {responseFormat}
                    </pre>
                  </div>
                  <div
                    className="absolute bottom-0 left-0 right-0 flex h-6 cursor-ns-resize items-center justify-center rounded-b-lg bg-muted/50 transition-colors hover:bg-muted"
                    onMouseDown={handleJsonSchemaMouseDown}
                  >
                    <div className="h-1 w-12 rounded-full bg-border" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <FormLabel className="flex items-center gap-2 text-sm font-medium">
              {t('BASE_PROMPT')}
              <InfoTooltip content={t('BASE_PROMPT_TOOLTIP')} />
            </FormLabel>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 rounded-[200px] border-primary text-primary hover:bg-primary/10"
              onClick={onOpenBasePromptAIModal}
            >
              <Brain className="h-4 w-4" />
              {t('AI')}
            </Button>
          </div>
          <FormField
            control={form.control}
            name="llm_config.base_prompt"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      value={basePromptValue}
                      placeholder={t('BASE_PROMPT_PLACEHOLDER')}
                      rows={6}
                      className="overflow-y-auto rounded-lg border border-border bg-muted/30 p-3 pb-8 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
                      style={{
                        height: '150px',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgb(156 163 175) transparent',
                      }}
                      onChange={handleTextareaChange}
                      onBlur={() => handleTextareaBlur(field)}
                      name={field.name}
                    />
                    <div
                      className="absolute bottom-0 left-0 right-0 flex h-6 cursor-ns-resize items-center justify-center rounded-b-lg bg-muted/50 transition-colors hover:bg-muted"
                      onMouseDown={handleMouseDown}
                    >
                      <div className="h-1 w-12 rounded-full bg-border" />
                      <span className="absolute right-3 rounded-lg bg-secondary px-2 py-1 text-xs text-medium-emphasis">
                        {t('TOKEN_COUNT', { count: tokenCount })}
                      </span>
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {isJsonEditorOpen && (
          <JsonEditorModal
            form={form}
            onClose={() => setIsJsonEditorOpen(false)}
            onSave={handleJsonSchemaSave}
            isMobile={isMobile}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
